import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import { FiUser, FiMapPin, FiPercent, FiEdit2, FiFileText } from "react-icons/fi";
import { AiOutlineEye } from "react-icons/ai";
import { getClientApi } from "../../../../Api/ClientWork/ClientWork";
import ActionButton from "../../../Common/ActionButton";
import CommonDetailModal from "../../../Common/CommonDetailModal";
import "../../../Accounts/Contracts/Contract/ContractDetailModal.css";
import "./ClientDetailModal.css";

/**
 * ClientDetailModal
 * Fully responsive modal matching ContractDetailModal layout
 * - Status badge at the right side of Client & Identity header
 * - Filer/Tax status badge at the right side of Commercial & Tax header
 * - 4-column space for WHT rates, retention, and KYC attachments view
 */
function ClientDetailModal({ isOpen, onClose, clientData, clientId }) {
  const history = useHistory();
  const [client, setClient] = useState(clientData || null);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const id = clientId || clientData?.Id;
    if (!id) {
      if (clientData) {
        setClient(clientData);
      }
      return;
    }

    let isMounted = true;
    (async () => {
      if (!clientData) {
        setLoading(true);
      }
      try {
        const response = await getClientApi(id);
        if (isMounted && response?.data?.IsSuccess && response.data.Data) {
          setClient(response.data.Data);
        }
      } catch (err) {
        if (isMounted && clientData) {
          setClient(clientData);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [clientId, clientData]);

  // Normalize documents array safely (handles stringified JSON, object, or array)
  const documentsList = useMemo(() => {
    const raw = client?.Documents;
    if (Array.isArray(raw)) {
      return raw.map((item, idx) => {
        if (typeof item === "string") {
          return {
            name: `Document-${idx + 1}`,
            url: item,
            size: "N/A",
            type: "Image / Document",
          };
        }
        return {
          name: item?.name || `Document-${idx + 1}`,
          url: item?.dataUrl || item?.url || item?.path || item?.filePath || "",
          size: item?.size || "N/A",
          type: item?.type || "Image / Document",
        };
      });
    }
    if (typeof raw === "string" && raw.trim() !== "") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map((item, idx) => ({
            name: item?.name || `Document-${idx + 1}`,
            url: item?.dataUrl || item?.url || item?.path || item?.filePath || "",
            size: item?.size || "N/A",
            type: item?.type || "Image / Document",
          }));
        }
      } catch (e) {
        return [
          {
            name: "Attached Document",
            url: raw,
            size: "N/A",
            type: "Image / Document",
          },
        ];
      }
    }
    return [];
  }, [client?.Documents]);

  if (!isOpen) {
    return null;
  }

  const CompactMetric = ({
    label,
    value,
    spanClass = "col-span-1",
    noLeftBorder = false,
    status = false,
  }) => (
    <div className={`client-detail-field ${spanClass} ${noLeftBorder ? "no-left-border" : ""}`}>
      <div className="contract-detail-field__label">{label}</div>
      <div className={status ? "contract-detail-status" : "contract-detail-field__value"}>
        {value != null && value !== "" ? value : "N/A"}
      </div>
    </div>
  );

  const DetailSectionHeader = ({ title, Icon, rightContent }) => (
    <div
      className="contract-detail-card__header d-flex justify-content-between align-items-center"
      style={{ minHeight: "44px", padding: "8px 14px" }}
    >
      <div className="d-flex align-items-center" style={{ gap: "9px" }}>
        <span className="contract-detail-card__icon">{Icon && <Icon />}</span>
        <b className="m-0" style={{ fontSize: "14px" }}>
          {title}
        </b>
      </div>
      {rightContent && <div className="d-flex align-items-center">{rightContent}</div>}
    </div>
  );

  const renderStatusChip = (status) => {
    const isActive = (status || "").toLowerCase() === "active";
    return (
      <span
        className="badge"
        style={{
          backgroundColor: isActive ? "#d4edda" : "#fff3cd",
          color: isActive ? "#155724" : "#856404",
          border: isActive ? "1px solid #c3e6cb" : "1px solid #ffeeba",
          borderRadius: "14px",
          padding: "4px 12px",
          fontSize: "11px",
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          textTransform: "capitalize",
          lineHeight: 1.2,
        }}
      >
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: isActive ? "#28a745" : "#ffc107",
            marginRight: "6px",
            display: "inline-block",
          }}
        />
        {status || "Active"}
      </span>
    );
  };

  const renderTaxStatusBadge = (taxStatus) => {
    const isFiler = (taxStatus || "").toLowerCase() === "filer";
    return (
      <span
        className="badge"
        style={{
          backgroundColor: isFiler ? "#e6f4ea" : "#fce8e6",
          color: isFiler ? "#137333" : "#c5221f",
          border: isFiler ? "1px solid #ceead6" : "1px solid #fad2cf",
          borderRadius: "14px",
          padding: "4px 12px",
          fontSize: "11px",
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          lineHeight: 1.2,
        }}
      >
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: isFiler ? "#137333" : "#c5221f",
            marginRight: "6px",
            display: "inline-block",
          }}
        />
        {taxStatus || "N/A"}
      </span>
    );
  };

  const renderGuaranteeBadge = (req) => {
    const isYes = (req || "").toLowerCase() === "yes";
    return (
      <span
        className="badge"
        style={{
          backgroundColor: isYes ? "#e0f2fe" : "#f1f5f9",
          color: isYes ? "#0369a1" : "#475569",
          border: isYes ? "1px solid #bae6fd" : "1px solid #e2e8f0",
          borderRadius: "12px",
          padding: "3px 9px",
          fontSize: "11px",
          fontWeight: 600,
          display: "inline-block",
        }}
      >
        {isYes ? "Required (Yes)" : "Not Required (No)"}
      </span>
    );
  };

  const handleOpenImage = (doc) => {
    setPreviewImage(doc);
  };

  return (
    <>
      <CommonDetailModal
        isOpen={isOpen}
        onClose={onClose}
        title="Client Detail"
        maxWidth="1040px"
        contentClassName="modal-content-screen1 contract-detail-modal client-detail-modal"
        topContent={
          <div className="client-detail-content">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "180px" }}
              >
                <ThreeDots color="#5cd1d0" height={40} width={40} />
              </div>
            ) : (
              <>
                {/* ── ROW 1: Client Profile & Contact Information ── */}
                <div className="row mt-2">
                  {/* Card 1: Client & Identity Profile */}
                  <div className="col-lg-6 col-12 mb-3 client-detail-column">
                    <div className="contract-detail-card h-100">
                      <DetailSectionHeader
                        title="Client & Identity Details"
                        Icon={FiUser}
                        rightContent={renderStatusChip(client?.Status)}
                      />
                      <div className="client-detail-metrics">
                        <CompactMetric
                          label="Owner / Company Name"
                          value={client?.OwnerName}
                          spanClass="col-span-full"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Contact Person"
                          value={client?.ContactPerson}
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Designation"
                          value={client?.Designation}
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="Business Type"
                          value={client?.BusinessType}
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="Email"
                          value={client?.Email}
                          spanClass="col-span-full"
                          noLeftBorder
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Contact & Address */}
                  <div className="col-lg-6 col-12 mb-3 client-detail-column">
                    <div className="contract-detail-card h-100">
                      <DetailSectionHeader title="Contact & Address" Icon={FiMapPin} />
                      <div className="client-detail-metrics">
                        <CompactMetric
                          label="Mobile / WhatsApp"
                          value={client?.Mobile}
                          spanClass="col-span-2"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Office Landline"
                          value={client?.OfficeLandline || "N/A"}
                          spanClass="col-span-1"
                        />

                        <CompactMetric
                          label="City"
                          value={client?.City}
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="State / Province"
                          value={client?.StateProvince}
                          spanClass="col-span-1"
                        />
                        <CompactMetric label="Country" value="Pakistan" spanClass="col-span-1" />
                        <CompactMetric
                          label="Official Business Address"
                          value={client?.OfficialAddress}
                          spanClass="col-span-full"
                          noLeftBorder
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── ROW 2: Commercial & Legal Terms (4 Columns Space) ── */}
                <div className="row">
                  {/* Card 3: Commercial & Tax Terms */}
                  <div className="col-12 mb-3 client-detail-column">
                    <div className="contract-detail-card h-100">
                      <DetailSectionHeader
                        title="Commercial & Tax Terms"
                        Icon={FiPercent}
                        rightContent={renderTaxStatusBadge(client?.TaxStatus)}
                      />
                      <div className="client-detail-metrics-4col">
                        {/* 4-Col Row 1 */}
                        <CompactMetric
                          label="NTN Number"
                          value={client?.NTN || "N/A"}
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Sales Tax Authority"
                          value={client?.SalesTaxStatus || "N/A"}
                          spanClass="col-span-2"
                        />
                        <CompactMetric
                          label="Guarantee Requirement"
                          value={renderGuaranteeBadge(client?.PerformanceGuaranteeRequire)}
                          spanClass="col-span-1"
                        />

                        {/* 4-Col Row 2 */}
                        <CompactMetric
                          label="Income Tax WHT Rate"
                          value={
                            client?.IncomeTaxWhtRate != null ? `${client.IncomeTaxWhtRate}%` : "N/A"
                          }
                          spanClass="col-span-1"
                          noLeftBorder
                        />
                        <CompactMetric
                          label="Sales Tax WHT Rate"
                          value={
                            client?.SalesTaxWhtRate != null ? `${client.SalesTaxWhtRate}%` : "N/A"
                          }
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="Default Retention Rate"
                          value={
                            client?.DefaultRetentionRate != null
                              ? `${client.DefaultRetentionRate}%`
                              : "N/A"
                          }
                          spanClass="col-span-1"
                        />
                        <CompactMetric
                          label="KYC Attachments"
                          value={
                            documentsList.length > 0 ? (
                              <div
                                className="d-flex flex-wrap align-items-center mt-1"
                                style={{ gap: "6px" }}
                              >
                                {documentsList.map((doc, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="btn btn-sm btn-outline-info d-inline-flex align-items-center"
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      padding: "2px 8px",
                                      height: "26px",
                                      borderRadius: "6px",
                                      lineHeight: 1,
                                    }}
                                    title={`Click to view ${doc.name}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenImage(doc);
                                    }}
                                  >
                                    <AiOutlineEye className="mr-1" size="1.25em" />
                                    <span>
                                      View{" "}
                                      {documentsList.length === 1 ? "Image" : `Image ${idx + 1}`}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px", fontStyle: "italic" }}
                              >
                                No image uploaded
                              </span>
                            )
                          }
                          spanClass="col-span-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Footer Action Buttons ── */}
                <div className="row mt-3">
                  <div
                    className="col-12 d-flex flex-wrap justify-content-end"
                    style={{ gap: "10px" }}
                  >
                    <ActionButton
                      text="Edit Client"
                      icon={FiEdit2}
                      variant="save"
                      onClick={() => {
                        onClose();
                        history.push(`/client-work/clients/edit/${client?.Id}`);
                      }}
                    />
                    <ActionButton text="Close" variant="grey" onClick={onClose} />
                  </div>
                </div>
              </>
            )}
          </div>
        }
      />

      {/* ── Lightbox Image Viewer Modal ── */}
      {previewImage && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.78)",
            zIndex: 1099,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "850px",
              width: "100%",
              maxHeight: "90vh",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className="d-flex justify-content-between align-items-center px-3"
              style={{
                minHeight: "52px",
                backgroundColor: "#075e5c",
                color: "#ffffff",
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <FiFileText size={18} />
                <span style={{ fontWeight: 600, fontSize: "14px" }}>
                  {previewImage.name || "Document Preview"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewImage(null)}
                style={{
                  background: "rgba(255,255,255,0.18)",
                  border: "none",
                  color: "#ffffff",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  fontSize: "20px",
                  lineHeight: "26px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                title="Close"
              >
                &times;
              </button>
            </div>

            {/* Modal Body / Image Viewer */}
            <div
              className="p-3 d-flex justify-content-center align-items-center"
              style={{
                minHeight: "260px",
                maxHeight: "calc(90vh - 120px)",
                overflow: "auto",
                backgroundColor: "#f4f8f8",
              }}
            >
              {previewImage.url ? (
                <img
                  src={previewImage.url}
                  alt={previewImage.name || "Attached file preview"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: "6px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const fallback = document.getElementById("preview-fallback-msg");
                    if (fallback) {
                      fallback.style.display = "block";
                    }
                  }}
                />
              ) : null}

              <div
                id="preview-fallback-msg"
                style={{
                  display: previewImage.url ? "none" : "block",
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#556e6f",
                }}
              >
                <FiFileText size={48} className="mb-2 text-muted" />
                <h6 style={{ fontWeight: 600 }}>{previewImage.name || "Attachment"}</h6>
                <p className="small mb-0 text-muted">
                  Document attached. No direct image preview available.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="d-flex justify-content-end align-items-center p-2 px-3 bg-light border-top"
              style={{ gap: "10px" }}
            >
              {previewImage.url && (
                <a
                  href={previewImage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={previewImage.name || "attachment"}
                  className="btn btn-sm btn-info d-inline-flex align-items-center"
                  style={{ fontSize: "12px", padding: "5px 12px" }}
                >
                  Download / Open Full Size
                </a>
              )}
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                style={{ fontSize: "12px", padding: "5px 14px" }}
                onClick={() => setPreviewImage(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ClientDetailModal;
